<template>
  <article class="condition-group" :class="`group-${colorIndex}`">
    <header class="group-header">
      <div class="group-title">
        <strong>GROUP {{ index + 1 }}</strong>
        <span>All conditions in this group match using</span>
        <button
          v-if="canManageRules"
          class="match-operator"
          :class="operatorClass(group.match)"
          type="button"
          @click.stop="toggleMatch"
        >
          {{ group.match || 'AND' }}
        </button>
        <span
          v-else
          class="match-operator"
          :class="operatorClass(group.match)"
        >
          {{ group.match || 'AND' }}
        </span>
      </div>
      <div class="group-actions">
        <button
          v-if="canManageRules"
          class="text-btn add-condition-btn"
          type="button"
          @click="addCondition"
        >
          + Add condition
        </button>
        <button v-if="canManageRules" class="icon-btn delete-action" type="button" @click="$emit('remove', group.id)">
          <delete-icon width="14px" height="14px" />
        </button>
        <button class="icon-btn group-toggle-btn" type="button" @click="toggle">
          <chevron-icon
            class="chevron"
            :direction="group.expanded ? 'up' : 'down'"
          />
        </button>
      </div>
    </header>

    <div v-show="group.expanded" class="condition-list">
      <condition-row
        v-for="condition in group.conditions"
        :key="condition.id"
        :condition="condition"
        :group-conditions="group.conditions"
        :condition-groups="allGroups"
        :group-match="group.match"
        :current-group-index="index"
        :lists="lists"
        :selected-tab="selectedTab"
        :selected-config="selectedConfig"
        :validation="validation[condition.id] || {}"
        :add-attempt-error="showLastConditionError && condition.id === lastConditionId"
        :can-manage-rules="canManageRules"
        :is-dragging="draggedConditionId === condition.id"
        :is-drop-target="dragOverConditionId === condition.id"
        @update="updateCondition"
        @remove="removeCondition"
        @drag-start="handleConditionDragStart"
        @drag-end="handleConditionDragEnd"
        @drag-over="handleConditionDragOver"
        @drop="handleConditionDrop"
      />
    </div>
  </article>
</template>

<script>
import DeleteIcon from '@/components/common/icons/deleteIcon.vue'
import ChevronIcon from '@/components/common/icons/chevronIcon.vue'
import ConditionRow from './ConditionRow.vue'
import { MATERIAL_FIXED_FIELDS } from './configConstants'
import { clone, createCondition } from './configModel'
import { getConditionValidationState, getCrossGroupRestrictedFieldIds, getDefaultConditionSelection, getMaterialCondtions, optionValue } from './conditionHelper'
import config from '@/config'

export default {
  name: 'ConditionGroup',
  components: {
    ConditionRow,
    DeleteIcon,
    ChevronIcon
  },
  props: {
    group: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    lists: {
      type: Object,
      required: true
    },
    allGroups: {
      type: Array,
      default: () => []
    },
    selectedTab: {
      type: Object,
      required: true
    },
    selectedConfig: {
      type: [String, Object],
      default: ''
    },
    validation: {
      type: Object,
      default: () => ({})
    },
    canManageRules: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      draggedConditionId: null,
      dragOverConditionId: null,
      showAddConditionError: false
    }
  },
  computed: {
    colorIndex () {
      return (this.index % 6) + 1
    },
    primarySelectedSource () {
      return this.group.conditions[0]?.fieldType || null
    },
    primarySelectedSourceId () {
      return optionValue(this.primarySelectedSource)
    },
    incompleteConditionIds () {
      return this.group.conditions
        .filter(condition => Object.values(getConditionValidationState(condition)).some(Boolean))
        .map(condition => condition.id)
    },
    hasIncompleteConditions () {
      return this.incompleteConditionIds.length > 0
    },
    lastCondition () {
      return this.group.conditions[this.group.conditions.length - 1] || null
    },
    lastConditionId () {
      return this.lastCondition?.id || null
    },
    isLastConditionIncomplete () {
      if (!this.lastCondition) {
        return false
      }

      return Object.values(getConditionValidationState(this.lastCondition)).some(Boolean)
    },
    showLastConditionError () {
      return this.showAddConditionError && this.isLastConditionIncomplete
    }
  },
  methods: {
    createNextCondition () {
      const nextCondition = createCondition()

      if (this.selectedTab.name !== 'MATERIAL') {
        return nextCondition
      }

      if (this.primarySelectedSourceId === 'PLUGIN_CUSTOM_FIELDS') {
        nextCondition.fieldType = clone(this.primarySelectedSource)
        nextCondition.field = ''
        return nextCondition
      }

      if (this.primarySelectedSourceId) {
        const restrictedFieldIds = getCrossGroupRestrictedFieldIds({
          conditionGroups: this.allGroups,
          groupIndex: this.index,
          selectedSource: this.primarySelectedSource
        })
        const usedFieldIds = this.group.conditions
          .filter(condition => optionValue(condition.fieldType) === this.primarySelectedSourceId)
          .map(condition => optionValue(condition.field))
        const nextField = getMaterialCondtions({ selectedSource: this.primarySelectedSource })
          .filter((item) => {
            if (this.primarySelectedSourceId === 'STANDARD_MATERIAL_FORM' && item?.type === config.FORM_TYPE.TABLE) {
              return false
            }

            if (restrictedFieldIds.has(optionValue(item))) {
              return false
            }

            return (this.group.match || 'AND') === 'OR' || !usedFieldIds.includes(optionValue(item))
          })[0]

        nextCondition.fieldType = clone(this.primarySelectedSource)
        nextCondition.field = nextField ? clone(nextField) : {}
        return nextCondition
      }

      const defaults = this.selectedConfig && optionValue(this.selectedConfig) === 'MATERIAL_FIXED_FIELDS'
        ? getDefaultConditionSelection({
          selectedTab: this.selectedTab.name,
          selectedConfig: this.selectedConfig,
          sourceFieldTypes: this.lists.sourceFieldTypes,
          groupConditions: this.group.conditions,
          allowDuplicates: (this.group.match || 'AND') === 'OR'
        })
        : null
      if (defaults) {
        nextCondition.fieldType = defaults.fieldType
        nextCondition.field = defaults.field
        return nextCondition
      }

      const nextField = (this.group.match || 'AND') === 'OR'
        ? MATERIAL_FIXED_FIELDS[0]
        : MATERIAL_FIXED_FIELDS.find((item) => {
          const usedFieldIds = this.group.conditions
            .filter(condition => optionValue(condition.fieldType) === 'MATERIAL_FIXED_FIELDS')
            .map(condition => optionValue(condition.field))
          return !usedFieldIds.includes(optionValue(item))
        })

      nextCondition.field = nextField ? clone(nextField) : {}
      return nextCondition
    },
    emitUpdate (patch) {
      this.$emit('update', { ...clone(this.group), ...patch })
    },
    toggle () {
      this.emitUpdate({ expanded: !this.group.expanded })
    },
    toggleMatch () {
      this.$emit('request-match-change', {
        groupId: this.group.id,
        nextMatch: this.group.match === 'OR' ? 'AND' : 'OR'
      })
    },
    operatorClass (operator) {
      return (operator || 'AND').toLowerCase() === 'or' ? 'operator-or' : 'operator-and'
    },
    updateCondition (updatedCondition) {
      const next = clone(this.group)
      const index = next.conditions.findIndex(item => item.id === updatedCondition.id)
      if (index >= 0) next.conditions.splice(index, 1, updatedCondition)
      this.$emit('update', next)
    },
    addCondition () {
      this.showAddConditionError = true
      if (this.hasIncompleteConditions) {
        return
      }

      const next = clone(this.group)
      next.conditions.push(this.createNextCondition())
      this.showAddConditionError = false
      this.$emit('update', next)
    },
    removeCondition (id) {
      const next = clone(this.group)
      next.conditions = next.conditions.filter(item => item.id !== id)
      if (!next.conditions.length) {
        this.$emit('remove', this.group.id)
        return
      }
      this.$emit('update', next)
    },
    handleConditionDragStart (conditionId) {
      this.draggedConditionId = conditionId
    },
    handleConditionDragEnd () {
      this.draggedConditionId = null
      this.dragOverConditionId = null
    },
    handleConditionDragOver (conditionId) {
      this.dragOverConditionId = conditionId
    },
    handleConditionDrop (targetConditionId) {
      if (!this.draggedConditionId || this.draggedConditionId === targetConditionId) {
        this.draggedConditionId = null
        this.dragOverConditionId = null
        return
      }
      const next = clone(this.group)
      const fromIndex = next.conditions.findIndex(item => item.id === this.draggedConditionId)
      const toIndex = next.conditions.findIndex(item => item.id === targetConditionId)
      if (fromIndex < 0 || toIndex < 0) return
      const [movedCondition] = next.conditions.splice(fromIndex, 1)
      next.conditions.splice(toIndex, 0, movedCondition)
      this.draggedConditionId = null
      this.dragOverConditionId = null
      this.$emit('update', next)
    }
  },
  watch: {
    hasIncompleteConditions (nextValue) {
      if (!nextValue) {
        this.showAddConditionError = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.condition-group {
  margin-bottom: 14px;
  overflow: hidden;
  border: 1px solid #dfe5ed;
  border-left: 4px solid var(--group-color);
  border-radius: 6px;
  background: #ffffff;
}

.group-1 {
  --group-color: #2f80ff;
  --group-soft: #eef6ff;
}

.group-2 {
  --group-color: #b45cff;
  --group-soft: #fbf4ff;
}

.group-3 {
  --group-color: #f79009;
  --group-soft: #fff9e7;
}

.group-4 {
  --group-color: #12b76a;
  --group-soft: #ecfdf3;
}

.group-5 {
  --group-color: #e11d48;
  --group-soft: #fff1f3;
}

.group-6 {
  --group-color: #0891b2;
  --group-soft: #ecfeff;
}

.group-header,
.group-title,
.group-actions {
  display: flex;
  align-items: center;
}

.group-header {
  justify-content: space-between;
  gap: 12px;
  min-height: 44px;
  padding: 8px 12px;
  background: var(--group-soft);
}

.group-title {
  flex-wrap: wrap;
  gap: 8px;
  color: #667085;
  font-size: var(--text-xs1);

  strong {
    color: var(--group-color);
    font-size: var(--text-xs2);
    letter-spacing: 0.8px;
    font-weight: 500;
  }
}

.match-operator {
  min-width: 34px;
  height: 20px;
  border: 1px solid var(--operator-color);
  border-radius: 4px;
  background: var(--operator-soft);
  color: var(--operator-color);
  font-size: var(--text-xs2);
  font-weight: 500;
  cursor: pointer;
}

.operator-and {
  --operator-color: #246bfe;
  --operator-soft: #eef4ff;
}

.operator-or {
  --operator-color: #f79009;
  --operator-soft: #fff8e7;
}

.group-actions {
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.condition-list {
  padding: 10px 12px 8px;
  overflow-x: auto;
  overflow-y: hidden;
  max-width: 100%;
  scrollbar-gutter: stable;
}

.condition-list ::v-deep .condition-row {
  min-width: 700px;
}

.text-btn,
.icon-btn {
  border: 0;
  background: transparent;
  cursor: pointer;
}

.text-btn {
  color: #0e9384;
  font-size: var(--text-s);
  font-weight: 500;
}

.text-btn.add-condition-btn {
  color: #0e9384;
}

.text-btn--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.icon-btn {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: #98a2b3;
}

.delete-action:hover {
  background: #fff1f0;
}

.delete-action:hover ::v-deep .delete-icon {
  color: #f04438;
}

.chevron {
  color: #98a2b3;
}

@media (max-width: 900px) {
  .group-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .condition-list ::v-deep .condition-row {
    min-width: 0;
  }

}
</style>
